import { Telemetry } from '@amzn/ihm-telemetry-js-lib';

import { WeblabService } from '@services/WeblabService';

export abstract class Event {
    protected _id: string; // this is an internal ID that never changes, used by this framework
    protected eventId: string | null; // this is a public ID for event tracking
    protected name: string;
    protected startTime: number | null;
    protected endTime: number | null;
    protected duration: number | null;
    protected completed: boolean;
    protected aborted: boolean;
    protected numErrors: number | null;
    protected toolId: string | null;
    protected errMsg: string | null;

    private static generateId(): string {
        // TODO: replace with uuid v4() later?
        return Math.random().toString(36).substring(2, 15);
    }

    protected static getPayload(data: Record<string, unknown>): Record<string, unknown> {
        if (!data) return {};
        delete data._id; // fields we do not want to emit
        delete data.actionIds; // Removing actionIds array from tasks
        delete data.taskIds; // Removing taskIds array from workflow
        // Read at emit time so the label reflects whatever the tool has resolved by now, and so
        // the order of weblab fetch vs telemetry init is irrelevant. Shared by action, task and
        // workflow events.
        data.piloting = WeblabService.getEnrolments();
        return data;
    }

    private init() {
        this.eventId = null;
        this.startTime = null;
        this.endTime = null;
        this.duration = null;
        this.completed = false;
        this.aborted = false;
        this.numErrors = 0;
        this.toolId = null;
        this.errMsg = null;
    }

    protected constructor(name: string) {
        this.name = name;
        this._id = Event.generateId();
        this.init();
    }

    protected abstract save(): void;
    protected abstract setCurrent(): void;
    protected abstract delete(): void;

    public get id() {
        return this._id;
    }

    public get isComplete() {
        return this.completed;
    }

    public get workflowName() {
        return this.name;
    }

    protected hydrate(data: any) {
        this._id = data._id;
        this.eventId = data.eventId;
        this.name = data.name;
        this.startTime = data.startTime;
        this.endTime = data.endTime;
        this.duration = data.duration;
        this.completed = data.completed;
        this.aborted = data.aborted;
        this.numErrors = data.numErrors;
        this.toolId = data.toolId;
        this.errMsg = data.errMsg;
    }

    protected toJSON() {
        return {
            _id: this._id,
            eventId: this.eventId,
            name: this.name,
            startTime: this.startTime,
            endTime: this.endTime,
            duration: this.duration,
            completed: this.completed,
            aborted: this.aborted,
            numErrors: this.numErrors,
            toolId: this.toolId,
            errMsg: this.errMsg,
        };
    }

    // TODO: Currently, deleting an event is not implemented because events
    // can be restarted and are referenced from parents by their IDs
    // If this is desired, will the ATBase orchestrator or the derived stop() function delete the event?

    protected start() {
        if (!Telemetry.get()) {
            console.warn('Telemetry not available, skipping event start');
            return;
        }
        this.init();
        this.eventId = Event.generateId();
        this.startTime = new Date().getTime();
        this.toolId = Telemetry.getToolId();
    }

    protected stop(aborted: boolean) {
        if (!Telemetry.get()) {
            console.warn('Telemetry not available, skipping event stop');
            return;
        }
        // The subclasses are responsible for verifying that we
        // do not stop an event that has not started nor emit an event that has already ended
        this.endTime = new Date().getTime();
        this.duration = this.endTime - this.startTime;
        this.completed = true;
        this.aborted = aborted;
    }
}
